import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { useI18n } from "../context/I18nContext";
import { useToast } from "../components/Toast";

function calculateMetrics(heightCm, weightKg) {
  const height = Number(heightCm);
  const weight = Number(weightKg);
  if (!height || !weight) return null;
  const heightM = height / 100;
  const round = (value) => Math.round(value * 10) / 10;
  const bmi = round(weight / (heightM * heightM));
  const idealMinKg = round(18.5 * heightM * heightM);
  const idealMaxKg = round(24.9 * heightM * heightM);
  const idealTargetKg = round(22 * heightM * heightM);
  let bmiCategory = "healthy";
  if (bmi < 18.5) bmiCategory = "underweight";
  else if (bmi >= 30) bmiCategory = "obese";
  else if (bmi >= 25) bmiCategory = "overweight";
  return { bmi, bmiCategory, idealMinKg, idealMaxKg, idealTargetKg, targetDifferenceKg: round(weight - idealTargetKg) };
}

function currentWeekStart() {
  const date = new Date();
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return date.toISOString().slice(0, 10);
}

function escapePdf(text = "") {
  return String(text).replace(/[^\x20-\x7EğüşöçıİĞÜŞÖÇ]/g, " ").replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function pdfChartCommands(entries = []) {
  if (!entries.length) return [];
  const chart = { x: 50, y: 560, width: 495, height: 140 };
  const weights = entries.map((entry) => Number(entry.weightKg));
  const heights = entries.map((entry) => Number(entry.heightCm));
  const all = [...weights, ...heights].filter(Number.isFinite);
  const min = Math.min(...all) - 2;
  const max = Math.max(...all) + 2;
  const x = (index) => chart.x + (entries.length === 1 ? 0 : (index / (entries.length - 1)) * chart.width);
  const y = (value) => chart.y + ((value - min) / Math.max(max - min, 1)) * chart.height;
  const path = (values) => values.map((value, index) => `${index === 0 ? "" : " "}${x(index).toFixed(1)} ${y(value).toFixed(1)} ${index === 0 ? "m" : "l"}`).join("");
  return [
    "0.75 0.75 0.75 RG 1 w",
    `${chart.x} ${chart.y} m ${chart.x + chart.width} ${chart.y} l S`,
    `${chart.x} ${chart.y} m ${chart.x} ${chart.y + chart.height} l S`,
    "0.91 0.44 0.23 RG 2 w",
    `${path(weights)} S`,
    "0.29 0.49 0.35 RG 2 w",
    `${path(heights)} S`,
    "0 0 0 RG"
  ];
}

function downloadPdf({ t, user, entries, review }) {
  const lines = [
    `${t("weeklyProgressReport")} - ${user.name || ""}`,
    `${t("generatedAt")}: ${new Date().toLocaleDateString()}`,
    "",
    `${t("weeklyProgress")} (${t("weightKg")} / ${t("heightCm")})`,
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    t("weeklyMeasurements"),
    ...entries.map((entry) => `${entry.weekStart}: ${entry.heightCm} cm, ${entry.weightKg} kg, BMI ${entry.bmi || entry.metrics?.bmi || "-"}`),
    "",
    t("aiWeeklyReview"),
    review?.review?.headline || "",
    review?.review?.summary || "",
    review?.review?.trend || "",
    "",
    t("recommendations"),
    ...(review?.review?.recommendations || []).map((item) => `- ${item}`),
    "",
    `${t("focusNextWeek")}: ${review?.review?.focusNextWeek || ""}`,
    `${t("medicalDisclaimer")}: ${review?.review?.caution || ""}`
  ];

  const textCommands = [
    "BT /F1 16 Tf 50 790 Td",
    ...lines.flatMap((line, index) => [
      index === 0 ? "" : "0 -20 Td",
      `(${escapePdf(line).slice(0, 95)}) Tj`
    ]).filter(Boolean),
    "ET"
  ];
  const content = [...textCommands, ...pdfChartCommands(entries)].join("\n");
  const objects = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj",
    "4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
    `5 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`
  ];
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `weekly-progress-${new Date().toISOString().slice(0, 10)}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function Profile({ navigate }) {
  const { state, actions } = useApp();
  const { t, language } = useI18n();
  const { toast } = useToast();
  const [draft, setDraft] = useState({
    heightCm: state.user?.heightCm || "",
    weightKg: state.user?.weightKg || "",
    pastIllnesses: state.user?.pastIllnesses || "",
    chronicDiseases: state.user?.chronicDiseases || "",
    allergies: state.user?.allergies || "",
    medications: state.user?.medications || ""
  });
  const [weeklyDraft, setWeeklyDraft] = useState({ weekStart: currentWeekStart(), heightCm: "", weightKg: "" });
  const [entries, setEntries] = useState([]);
  const [review, setReview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [weeklySaving, setWeeklySaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const metrics = useMemo(() => calculateMetrics(draft.heightCm, draft.weightKg), [draft.heightCm, draft.weightKg]);

  useEffect(() => {
    setDraft({
      heightCm: state.user?.heightCm || "",
      weightKg: state.user?.weightKg || "",
      pastIllnesses: state.user?.pastIllnesses || "",
      chronicDiseases: state.user?.chronicDiseases || "",
      allergies: state.user?.allergies || "",
      medications: state.user?.medications || ""
    });
  }, [state.user?.heightCm, state.user?.weightKg, state.user?.pastIllnesses, state.user?.chronicDiseases, state.user?.allergies, state.user?.medications]);

  useEffect(() => {
    if (!state.user?.id) return;
    actions.getWeeklyProfile()
      .then((data) => setEntries(data.entries || []))
      .catch(() => {});
  }, [state.user?.id]);

  const update = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));
  const updateWeekly = (key, value) => setWeeklyDraft((previous) => ({ ...previous, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await actions.updateProfile(draft);
      toast(t("profileSaved"), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const saveWeekly = async () => {
    setWeeklySaving(true);
    try {
      const data = await actions.saveWeeklyProfile(weeklyDraft);
      setEntries(data.entries || []);
      setReview(null);
      setWeeklyDraft({ weekStart: currentWeekStart(), heightCm: "", weightKg: "" });
      toast(t("weeklyMeasurementSaved"), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setWeeklySaving(false);
    }
  };

  const loadReview = async () => {
    setReviewing(true);
    try {
      const data = await actions.getWeeklyProfileReview({ weekStart: currentWeekStart(), lang: language });
      setReview(data);
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setReviewing(false);
    }
  };

  if (!state.user) {
    return (
      <div className="empty-state">
        <div className="empty-icon">Profil</div>
        <h3>{t("profileLoginRequired")}</h3>
        <p>{t("profileLoginHint")}</p>
        <button className="btn btn-outline" onClick={() => navigate("legal")}>{t("legalOpen")}</button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("myProfile")}</h1>
        <p>{t("profileIntro")}</p>
      </div>

      <section className="profile-coach-note legal-profile-card">
        <div>
          <strong>{t("legalTitle")}</strong>
          <span>{t("legalProfileHint")}</span>
        </div>
        <button className="btn btn-outline" onClick={() => navigate("legal")}>{t("legalOpen")}</button>
      </section>

      <section className="profile-layout">
        <div className="profile-form-panel">
          <div className="profile-user-row">
            <div className="profile-avatar">{String(state.user.name || "U").slice(0, 1).toUpperCase()}</div>
            <div>
              <strong>{state.user.name}</strong>
              <span>{state.user.email}</span>
            </div>
          </div>

          <div className="profile-grid">
            <NumberField label={t("heightCm")} value={draft.heightCm} onChange={(value) => update("heightCm", value)} min="80" max="260" placeholder="175" />
            <NumberField label={t("weightKg")} value={draft.weightKg} onChange={(value) => update("weightKg", value)} min="20" max="400" step="0.1" placeholder="72" />
          </div>

          <div className="profile-health-grid">
            <TextAreaField label={t("pastIllnesses")} value={draft.pastIllnesses} onChange={(value) => update("pastIllnesses", value)} placeholder={t("pastIllnessesPlaceholder")} />
            <TextAreaField label={t("chronicDiseases")} value={draft.chronicDiseases} onChange={(value) => update("chronicDiseases", value)} placeholder={t("chronicDiseasesPlaceholder")} />
            <TextAreaField label={t("allergies")} value={draft.allergies} onChange={(value) => update("allergies", value)} placeholder={t("allergiesPlaceholder")} />
            <TextAreaField label={t("medications")} value={draft.medications} onChange={(value) => update("medications", value)} placeholder={t("medicationsPlaceholder")} />
          </div>

          <p className="profile-note">{t("healthProfilePrivacyNote")}</p>

          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? t("saving") : t("saveProfile")}
          </button>
        </div>

        <div className="profile-metrics-panel">
          <h2>{t("idealWeightResult")}</h2>
          {metrics ? (
            <>
              <div className="metric-main">
                <span>{t("idealWeightRange")}</span>
                <strong>{metrics.idealMinKg} - {metrics.idealMaxKg} kg</strong>
              </div>
              <div className="metric-cards">
                <Metric label={t("bmi")} value={metrics.bmi} />
                <Metric label={t("bmiCategory")} value={t(metrics.bmiCategory)} />
                <Metric label={t("neutralTarget")} value={`${metrics.idealTargetKg} kg`} />
              </div>
              <p className="profile-note">{t("profileDietitianNote")}</p>
            </>
          ) : (
            <div className="empty-state compact">
              <div className="empty-icon">BMI</div>
              <h3>{t("enterHeightWeight")}</h3>
              <p>{t("enterHeightWeightHint")}</p>
            </div>
          )}
        </div>
      </section>

      <section className="weekly-panel">
        <div className="admin-recipe-head">
          <strong>{t("weeklyProgress")}</strong>
          <span>{t("weeklyProgressIntro")}</span>
        </div>

        <div className="weekly-form">
          <label>
            {t("weekStart")}
            <input type="date" value={weeklyDraft.weekStart} onChange={(event) => updateWeekly("weekStart", event.target.value)} />
          </label>
          <NumberField label={t("heightCm")} value={weeklyDraft.heightCm} onChange={(value) => updateWeekly("heightCm", value)} min="80" max="260" placeholder="175" />
          <NumberField label={t("weightKg")} value={weeklyDraft.weightKg} onChange={(value) => updateWeekly("weightKg", value)} min="20" max="400" step="0.1" placeholder="72" />
          <button className="btn btn-primary" onClick={saveWeekly} disabled={weeklySaving}>
            {weeklySaving ? t("saving") : t("saveWeeklyMeasurement")}
          </button>
        </div>

        {entries.length ? <WeeklyLineChart entries={entries} t={t} /> : (
          <div className="empty-state compact">
            <div className="empty-icon">↗</div>
            <h3>{t("noWeeklyData")}</h3>
            <p>{t("noWeeklyDataHint")}</p>
          </div>
        )}

        <div className="weekly-actions">
          <button className="btn btn-green" onClick={loadReview} disabled={reviewing || entries.length === 0}>
            {reviewing ? t("wait") : t("getWeeklyAiReview")}
          </button>
          <button className="btn btn-outline" onClick={() => downloadPdf({ t, user: state.user, entries, review })} disabled={!entries.length}>
            {t("downloadPdf")}
          </button>
        </div>

        {review?.review && (
          <div className="weekly-review">
            <strong>{review.review.headline}</strong>
            <p>{review.review.summary}</p>
            <span>{review.review.trend}</span>
            <div className="tip-list">
              {(review.review.recommendations || []).map((item, index) => <div key={index}>• {item}</div>)}
            </div>
            <em>{t("focusNextWeek")}: {review.review.focusNextWeek}</em>
            <small>{review.cached ? t("weeklyReviewCached") : t("weeklyReviewGenerated")}</small>
          </div>
        )}
      </section>
    </div>
  );
}

function NumberField({ label, value, onChange, ...props }) {
  return (
    <label>
      {label}
      <input type="number" value={value} onChange={(event) => onChange(event.target.value)} {...props} />
    </label>
  );
}

function TextAreaField({ label, value, onChange, ...props }) {
  return (
    <label>
      {label}
      <textarea value={value} onChange={(event) => onChange(event.target.value)} maxLength="500" rows="3" {...props} />
    </label>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function WeeklyLineChart({ entries, t }) {
  const width = 720;
  const height = 260;
  const padding = 42;
  const weights = entries.map((entry) => Number(entry.weightKg));
  const heights = entries.map((entry) => Number(entry.heightCm));
  const all = [...weights, ...heights].filter(Number.isFinite);
  const min = Math.min(...all) - 2;
  const max = Math.max(...all) + 2;
  const x = (index) => padding + (entries.length === 1 ? 0 : (index / (entries.length - 1)) * (width - padding * 2));
  const y = (value) => height - padding - ((value - min) / Math.max(max - min, 1)) * (height - padding * 2);
  const path = (values) => values.map((value, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(value)}`).join(" ");

  return (
    <div className="weekly-chart-card">
      <div className="chart-legend">
        <span className="weight-dot">{t("weightKg")}</span>
        <span className="height-dot">{t("heightCm")}</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t("weeklyProgress")}>
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} />
        <path className="weight-line" d={path(weights)} />
        <path className="height-line" d={path(heights)} />
        {entries.map((entry, index) => (
          <g key={entry.weekStart}>
            <circle className="weight-point" cx={x(index)} cy={y(entry.weightKg)} r="5" />
            <circle className="height-point" cx={x(index)} cy={y(entry.heightCm)} r="5" />
            <text x={x(index)} y={height - 12} textAnchor="middle">{entry.weekStart.slice(5)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}
